import type { ReactNode } from "react";
import { Copy } from "lucide-react";
import type { PizzaItem } from "@/api/pizza-checker/schemas";
import { formatCaptureCount, formatDailyCounter } from "@/lib/counter-format";
import { parsePizzaName } from "@/lib/order-items";
import { cn } from "@/lib/utils";
import { copyToClipboard } from "@/lib/clipboard";
import { formatDateTime } from "@/lib/date-format";
import { useLocalization } from "@/context/localization-context";

function Fact({ label, children, wide }: { label: string; children: ReactNode; wide?: boolean }) {
  return (
    <div className={cn("min-w-0", wide && "col-span-2")}>
      <dt className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-0.5 truncate text-sm">{children}</dd>
    </div>
  );
}

function IdValue({
  value,
  what,
  copyLabel,
  onOpen,
}: {
  value: string;
  /** what is copied, for the confirmation ("Pizza ID") */
  what: string;
  /** label of the copy button ("Copy pizza ID") */
  copyLabel: string;
  onOpen?: () => void;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      {onOpen ? (
        <button
          type="button"
          onClick={onOpen}
          className="min-w-0 truncate font-mono text-xs underline decoration-dotted underline-offset-2 hover:text-muted-foreground"
          title={value}
        >
          {value}
        </button>
      ) : (
        <span className="min-w-0 truncate font-mono text-xs" title={value}>
          {value}
        </span>
      )}
      <button
        type="button"
        onClick={() => copyToClipboard(value, what)}
        className="shrink-0 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
        aria-label={copyLabel}
      >
        <Copy className="h-3 w-3" />
      </button>
    </span>
  );
}

/** Where and when the pizza was captured, and its ids. */
export function PizzaFacts({
  pizza,
  branchName,
  orderDailyCounter,
  onOpenOrder,
  className,
}: {
  pizza?: PizzaItem | null;
  branchName?: string | null;
  orderDailyCounter?: string | number | null;
  onOpenOrder?: () => void;
  className?: string;
}) {
  const { t } = useLocalization();
  const { typeId, name } = parsePizzaName(pizza?.name);
  const na = t("common.notAvailable");

  return (
    <section className={cn("rounded-xl border bg-card px-4 py-3", className)} aria-label={t("common.details")}>
      <h3 className="mb-2 text-sm font-semibold">{t("common.details")}</h3>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-2.5">
        <Fact label={t("common.pizza")} wide>
          <span title={pizza?.name}>{name || na}</span>
        </Fact>
        <Fact label={t("detail.facts.type")}>{typeId ?? na}</Fact>
        <Fact label={t("common.branch")}>{branchName || na}</Fact>
        <Fact label={t("common.order")}>
          {pizza?.order_id && onOpenOrder ? (
            <button
              type="button"
              onClick={onOpenOrder}
              className="font-medium underline decoration-dotted underline-offset-2 hover:text-muted-foreground"
            >
              {formatDailyCounter(orderDailyCounter)}
            </button>
          ) : (
            formatDailyCounter(orderDailyCounter)
          )}
        </Fact>
        <Fact label={t("detail.facts.captures")}>{pizza ? formatCaptureCount(pizza.capture_count) : na}</Fact>
        <Fact label={t("detail.facts.captured")} wide>
          {pizza?.capture_at ? formatDateTime(pizza.capture_at) : na}
        </Fact>
        <Fact label={t("detail.pizzaId")} wide>
          {pizza?.id ? (
            <IdValue value={pizza.id} what={t("detail.pizzaId")} copyLabel={t("detail.copyPizzaId")} />
          ) : (
            na
          )}
        </Fact>
        <Fact label={t("detail.orderId")} wide>
          {pizza?.order_id ? (
            <IdValue
              value={pizza.order_id}
              what={t("detail.orderId")}
              copyLabel={t("detail.copyOrderId")}
              onOpen={onOpenOrder}
            />
          ) : (
            na
          )}
        </Fact>
      </dl>
    </section>
  );
}
